'use client';

import { useEffect, useCallback, useState } from 'react';
import { useTimeLeft } from './use-time-left';

export interface UseEnergyCountdownOptions {
  endDate: string | undefined;
  onEndCountDown: () => void;
  refreshInterval?: number;
}

export const useEnergyCountdown = ({
  endDate,
  onEndCountDown,
  refreshInterval = 1000, // Default to 1 second
}: UseEnergyCountdownOptions) => {
  const { getTimeText, ended } = useTimeLeft(endDate);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleEndCountDown = useCallback(() => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      onEndCountDown();
    } catch (error) {
      console.error('Error in energy countdown callback:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [onEndCountDown, isRefreshing]);

  useEffect(() => {
    if (ended) {
      // Add a small delay to ensure the timer has fully ended
      const timeoutId = setTimeout(() => {
        handleEndCountDown();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [ended, handleEndCountDown]);

  // Set up periodic refresh when countdown is active
  useEffect(() => {
    if (!endDate || ended) return;

    const intervalId = setInterval(() => {
      // Only trigger refresh if we're within 5 minutes of the end time
      const now = Date.now();
      const endTime = new Date(endDate).getTime();
      const timeUntilEnd = endTime - now;

      if (timeUntilEnd > 0 && timeUntilEnd <= 5 * 60 * 1000) {
        // Force a refresh when we're close to the end
        handleEndCountDown();
      }
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [endDate, ended, refreshInterval, handleEndCountDown]);

  return {
    getTimeText,
    ended,
    isRefreshing,
  };
};
